// @file Helper for Whiteboard (tools, colors, etc)
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { tailwindFlattenedColors } from '@@/bits/tailwind'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { type WhiteboardTool } from '@@/types/whiteboard'
import type { IndexKey, JsonObject, TLPage, TLPageId, TLRecord, TLShape } from 'tldraw'
import { DefaultColorThemePalette, getIndexAbove, getIndexBelow, getIndexBetween, PageRecordType } from 'tldraw'

const CANVAS_HEIGHT = 1080
const CANVAS_WIDTH = 1920
const MAX_FRAME_LIMIT = 100

const HIGHLIGHTER_COLORS = ['neon-pink', 'neon-orange', 'neon-yellow', 'neon-green', 'neon-blue', 'grey-200'] as const
const TEXT_COLORS = ['scarlet-500', 'canary-500', 'park-500', 'oceanic-500', 'dark-text-100', 'light-text-100'] as const
const DRAW_COLORS = ['scarlet-500', 'canary-500', 'park-500', 'oceanic-500', 'dark-text-100', 'light-text-100'] as const
const NOTES_COLORS = ['scarlet-200', 'canary-200', 'park-200', 'oceanic-200', 'grape-200', 'light-ui-300'] as const
const SHAPE_BORDER_COLORS = [
  'scarlet-500', // red
  'tangerine-600', //  orange
  'canary-600', // yellow
  'oceanic-400', // blue
  'teal-500', //  teal
  'park-500', // green
  'whiteboard-shape-border-grape', //  purple
  'light-text-100', // white
  'dark-text-100', // black
] as const

const SHAPE_BORDER_COLORS_UNUSED = [
  'park-600', // Old green (light-green)

  // 'padlet-pink-500', // Experimental pink
] as const

const SHAPE_FILL_COLORS = [
  'scarlet-100', // red
  'tangerine-100', // orange
  'canary-100', // yellow
  'whiteboard-shape-fill-park', // green
  'teal-100', // teal
  'oceanic-100', // blue
  'grape-100', // purple
  'grey-200', // black
  'grey-50', // white
  // 'padlet-pink-50', // Experimental pink #FFE0EC
] as const

const SHAPE_BORDER_TO_FAKE_FILL_COLOR_MAP: Record<typeof SHAPE_BORDER_COLORS[number], string> = {
  'scarlet-500': 'scarlet-200', // red
  'tangerine-600': 'tangerine-200', // orange
  'canary-600': 'canary-200', // yellow
  'park-500': 'whiteboard-shape-fill-park', // green
  'teal-500': 'teal-200', // teal
  'oceanic-400': 'oceanic-200', // blue
  'whiteboard-shape-border-grape': 'grape-200', // purple
  'dark-text-100': 'grey-400', // black
  'light-text-100': 'grey-200', // white
}

const SHAPE_BORDER_TO_FAKE_BORDER_COLOR_MAP: Partial<Record<typeof SHAPE_BORDER_COLORS[number], string>> = {
  'whiteboard-shape-border-grape': 'grape-500', // purple
}
const DEFAULT_COLORS = {
  highlighter: HIGHLIGHTER_COLORS[2],
  text: TEXT_COLORS[4],
  draw: DRAW_COLORS[4],
  note: NOTES_COLORS[1],
  shapeBorder: SHAPE_BORDER_COLORS[4],
} as const

type ToolTailwindColorType =
  | typeof HIGHLIGHTER_COLORS[number]
  | typeof TEXT_COLORS[number]
  | typeof DRAW_COLORS[number]
  | typeof NOTES_COLORS[number]
  | typeof SHAPE_BORDER_COLORS[number]
  | typeof SHAPE_FILL_COLORS[number]
  | typeof SHAPE_BORDER_COLORS_UNUSED[number]
type AllToolTailwindColorListType =
  | typeof HIGHLIGHTER_COLORS
  | typeof TEXT_COLORS
  | typeof DRAW_COLORS
  | typeof NOTES_COLORS
  | typeof SHAPE_BORDER_COLORS
  | typeof SHAPE_FILL_COLORS
  | typeof SHAPE_BORDER_COLORS_UNUSED
type TldrawColorVariantType = 'highlight' | 'solid' | 'note' | 'semi'

// @see https://github.com/tldraw/tldraw/blob/master/packages/tlschema/src/styles/TLColorStyle.ts
export type TldrawDefaultColorStyleColor =
  | 'black'
  | 'grey'
  | 'light-violet'
  | 'violet'
  | 'blue'
  | 'light-blue'
  | 'yellow'
  | 'orange'
  | 'green'
  | 'light-green'
  | 'light-red'
  | 'red'
  | 'white'

// Highlighter colors
const HIGHLIGHT_COLOR_MAP: Record<typeof HIGHLIGHTER_COLORS[number], TldrawDefaultColorStyleColor> = {
  'neon-pink': 'light-violet',
  'neon-yellow': 'yellow',
  'neon-orange': 'orange',
  'neon-green': 'light-green',
  'neon-blue': 'light-blue',
  'grey-200': 'grey',
}
// Text colors
// Draw colors
// Shape border colors
const SOLID_COLOR_MAP: Record<
  | typeof TEXT_COLORS[number]
  | typeof DRAW_COLORS[number]
  | typeof SHAPE_BORDER_COLORS[number]
  | typeof SHAPE_BORDER_COLORS_UNUSED[number],
  TldrawDefaultColorStyleColor
> = {
  'scarlet-500': 'red', //        draw, text, shape: red
  'tangerine-600': 'orange', //   shape: orange
  'canary-500': 'light-red', //   draw, text: yellow // Use light-red for canary-500 because we have 2 shades of yellow and there is no light-yellow in tldraw
  'canary-600': 'yellow', //      shape: yellow
  'park-500': 'light-green', //         draw, text, shape: green
  'park-600': 'green', //   shape: light-green
  'teal-500': 'light-violet', //          shape: teal
  'oceanic-500': 'blue', //       draw, text: blue
  'oceanic-400': 'light-blue', // shape: blue
  'dark-text-100': 'black', //    draw, text, shape: black
  'light-text-100': 'white', //   draw, text, shape: white
  'whiteboard-shape-border-grape': 'violet', //       shape: purple
}
// Note colors
const NOTE_COLOR_MAP: Record<typeof NOTES_COLORS[number], TldrawDefaultColorStyleColor> = {
  'scarlet-200': 'light-red',
  'canary-200': 'yellow',
  'park-200': 'light-green',
  'oceanic-200': 'light-blue',
  'grape-200': 'violet',
  'light-ui-300': 'grey',
}
// Shape fill colors
const SEMI_COLOR_MAP: Record<typeof SHAPE_FILL_COLORS[number], TldrawDefaultColorStyleColor> = {
  'scarlet-100': 'red',
  'tangerine-100': 'orange',
  'canary-100': 'yellow',
  'whiteboard-shape-fill-park': 'light-green',
  'teal-100': 'light-violet',
  'oceanic-100': 'light-blue',
  'grape-100': 'violet',
  'grey-200': 'black',
  'grey-50': 'white',
}

const TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR: Record<
  TldrawColorVariantType,
  Partial<Record<ToolTailwindColorType, TldrawDefaultColorStyleColor>>
> = {
  highlight: HIGHLIGHT_COLOR_MAP,
  solid: SOLID_COLOR_MAP,
  note: NOTE_COLOR_MAP,
  semi: SEMI_COLOR_MAP,
}

// Map extra colors to the closest tldraw default color style color
// This is for Jamboard shapes color mapping
const EXTRA_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR: Record<
  TldrawColorVariantType,
  Partial<Record<string, TldrawDefaultColorStyleColor>>
> = {
  highlight: {
    'grey-200': 'black', // Jamboard shape border color
    'canary-500': 'light-red',
  },
  solid: {},
  note: {},
  semi: {
    'oceanic-300': 'blue', // Jamboard shape fill color
    'grape-100': 'violet',
    'park-100': 'green',
    // 'grey-200': 'white',
  },
}

// Only the cone currently in use
export type TldrawGeoShapeGeoStyle = 'ellipse' | 'rectangle' | 'cloud' | 'oval' | 'arrow-right' | 'star'
/**
 * @privateRemarks
 * Currently, tldraw does not support hex colors, so we need to map our colors to the closest tldraw default color style color
 * This is a temporary solution until we patch tldraw to support hex colors or tldraw adds support for custom colors
 **/
function closestTldrawDefaultColorStyleColor(
  toolId: string,
  toolColor: ToolTailwindColorType,
): TldrawDefaultColorStyleColor {
  let colorCategory: TldrawColorVariantType = 'solid'
  switch (toolId) {
    case 'highlight':
      colorCategory = 'highlight'
      break
    case 'text':
    case 'draw':
    case 'shape':
      colorCategory = 'solid'
      break
    case 'note':
      colorCategory = 'note'
      break
    default:
      break
  }
  const nonTldrawColors = ['default', 'dark-text-100']
  if (nonTldrawColors.includes(toolColor)) {
    if (toolId === 'note') {
      return 'grey'
    }
    return 'black'
  }
  return TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR[colorCategory][toolColor] as TldrawDefaultColorStyleColor
}

type SolidColor = string
interface HighlightColor {
  srgb: string
  p3: string
}
interface NoteColor {
  fill: string
  text: string
}
function changeTldrawDefaultColorStyleColor(
  color: string,
  variant: 'solid' | 'highlight' | 'note' | 'semi',
  toolColor: ToolTailwindColorType,
): void {
  // For darkmode, we are setting to the same color as light mode since we're ignoring dark mode for now in v1
  // TODO: Add dark mode colorset
  for (const mode of ['lightMode', 'darkMode'] as const) {
    let targetColor: SolidColor | HighlightColor | NoteColor = '#000000'

    if (variant === 'solid' || variant === 'semi') {
      targetColor = tailwindFlattenedColors[toolColor]
    }
    if (variant === 'highlight') {
      targetColor = {
        srgb: tailwindFlattenedColors[toolColor],
        p3: tailwindFlattenedColors[toolColor],
      }
    }
    if (variant === 'note') {
      targetColor = {
        fill: tailwindFlattenedColors[toolColor],
        text: '#000000',
      }
    }
    DefaultColorThemePalette[mode][color][variant] = targetColor
  }
}

function changeAllTldrawDefaultColorStyleColor(): void {
  const variants = Object.keys(TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR) as TldrawColorVariantType[]
  variants.forEach((variant) => {
    const colors = TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR[variant]
    const tailwindColors = Object.keys(colors) as ToolTailwindColorType[]
    tailwindColors.forEach((tailwindColor) => {
      const tldrawColor = colors[tailwindColor]
      if (tldrawColor == null) {
        return
      }
      changeTldrawDefaultColorStyleColor(tldrawColor, variant as 'solid' | 'highlight' | 'note' | 'semi', tailwindColor)
    })
  })
  const variantsForExtraColors = Object.keys(
    EXTRA_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR,
  ) as TldrawColorVariantType[]
  variantsForExtraColors.forEach((variant) => {
    const colors = EXTRA_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR[variant]
    const tailwindColors = Object.keys(colors) as ToolTailwindColorType[]
    tailwindColors.forEach((tailwindColor) => {
      const tldrawColor = colors[tailwindColor]
      if (tldrawColor == null) {
        return
      }
      changeTldrawDefaultColorStyleColor(tldrawColor, variant as 'solid' | 'highlight' | 'note' | 'semi', tailwindColor)
    })
  })
}
function getTailwindBgColorClass(toolId: string): string | null {
  const color = toolId

  switch (color) {
    case 'neon-pink':
      return 'bg-neon-pink'
    case 'neon-yellow':
      return 'bg-neon-yellow'
    case 'neon-orange':
      return 'bg-neon-orange'
    case 'neon-green':
      return 'bg-neon-green'
    case 'neon-blue':
      return 'bg-neon-blue'
    case 'grey-200':
      return 'bg-grey-200'
    case 'scarlet-500':
      return 'bg-scarlet-500'
    case 'canary-500':
      return 'bg-canary-500'
    case 'park-500':
      return 'bg-park-500'
    case 'oceanic-500':
      return 'bg-oceanic-500'
    case 'oceanic-400':
      return 'bg-oceanic-400'
    case 'grape-200':
      return 'bg-grape-200'
    case 'oceanic-200':
      return 'bg-oceanic-200'
    case 'canary-200':
      return 'bg-canary-200'
    case 'park-200':
      return 'bg-park-200'
    case 'scarlet-200':
      return 'bg-scarlet-200'
    case 'whiteboard-shape-border-grape':
      return 'bg-whiteboard-shape-border-grape'
    case 'grape-500':
      return 'bg-grape-500'
    case 'tangerine-600':
      return 'bg-tangerine-600'
    case 'canary-600':
      return 'bg-canary-600'
    case 'park-600':
      return 'bg-park-600'
    case 'teal-500':
      return 'bg-teal-500'
    case 'dark-text-100':
      return 'bg-dark-text-100'
    case 'light-text-100':
      return 'bg-light-text-100'
    default:
      return null
  }
}

function getTailwindColorClass(toolId: string): string | null {
  const color = toolId

  switch (color) {
    case 'neon-pink':
      return 'text-neon-pink'
    case 'neon-yellow':
      return 'text-neon-yellow'
    case 'neon-orange':
      return 'text-neon-orange'
    case 'neon-green':
      return 'text-neon-green'
    case 'neon-blue':
      return 'text-neon-blue'
    case 'grey-200':
      return 'text-grey-200'
    case 'scarlet-500':
      return 'text-scarlet-500'
    case 'canary-500':
      return 'text-canary-500'
    case 'park-500':
      return 'text-park-500'
    case 'oceanic-500':
      return 'text-oceanic-500'
    case 'oceanic-400':
      return 'text-oceanic-400'
    case 'grape-200':
      return 'text-grape-200'
    case 'oceanic-200':
      return 'text-oceanic-200'
    case 'canary-200':
      return 'text-canary-200'
    case 'park-200':
      return 'text-park-200'
    case 'scarlet-200':
      return 'text-scarlet-200'
    case 'whiteboard-shape-border-grape':
      return 'text-whiteboard-shape-border-grape'
    case 'grape-500':
      return 'text-grape-500'
    case 'tangerine-600':
      return 'text-tangerine-600'
    case 'canary-600':
      return 'text-canary-600'
    case 'park-600':
      return 'text-park-600'
    case 'teal-500':
      return 'text-teal-500'
    case 'teal-200':
      return 'text-teal-200'
    case 'teal-100':
      return 'text-teal-100'
    case 'scarlet-100':
      return 'text-scarlet-100'
    case 'canary-100':
      return 'text-canary-100'
    case 'whiteboard-shape-fill-park':
      return 'text-whiteboard-shape-fill-park'
    case 'oceanic-100':
      return 'text-oceanic-100'
    case 'grey-100':
      return 'text-grey-100'
    case 'grey-400':
      return 'text-grey-400'
    case 'grey-300':
      return 'text-grey-300'
    case 'dark-text-100':
      return 'text-dark-text-100'
    case 'light-text-100':
      return 'text-light-text-100'
    default:
      return null
  }
}

function getTailwindRingColorClass(toolId: string): string | null {
  const color = toolId

  switch (color) {
    case 'neon-pink':
      return 'ring-neon-pink'
    case 'neon-yellow':
      return 'ring-neon-yellow'
    case 'neon-orange':
      return 'ring-neon-orange'
    case 'neon-green':
      return 'ring-neon-green'
    case 'neon-blue':
      return 'ring-neon-blue'
    case 'grey-200':
      return 'ring-grey-200'
    case 'scarlet-500':
      return 'ring-scarlet-500'
    case 'canary-500':
      return 'ring-canary-500'
    case 'park-500':
      return 'ring-park-500'
    case 'oceanic-500':
      return 'ring-oceanic-500'
    case 'grape-200':
      return 'ring-grape-200'
    case 'oceanic-200':
      return 'ring-oceanic-200'
    case 'canary-200':
      return 'ring-canary-200'
    case 'park-200':
      return 'ring-park-200'
    case 'scarlet-200':
      return 'ring-scarlet-200'
    case 'grape-600':
      return 'ring-grape-600'
    case 'tangerine-600':
      return 'ring-tangerine-600'
    case 'canary-600':
      return 'ring-canary-600'
    case 'park-600':
      return 'ring-park-600'
    case 'dark-text-100':
      return 'ring-dark-text-100'
    case 'light-text-100':
      return 'ring-light-text-100'
    default:
      return null
  }
}

function createSecondaryMenuFromColors(toolId: string, colors: AllToolTailwindColorListType): WhiteboardTool[] {
  return colors.map((color: ToolTailwindColorType) => ({
    label: '', // TODO: Add color name for accessibility label
    iconName: 'color',
    id: color,
    // tldrawToolId: tailwindFlattenedColors[color], // TODO: Patch tldraw to support hex colors
    tldrawToolId: closestTldrawDefaultColorStyleColor(toolId, color),
  }))
}

const geoToolColors: WhiteboardTool[] = SHAPE_BORDER_COLORS.map((color) => ({
  label: '', // TODO: Add color name for accessibility label
  iconName: 'color',
  id: color,
  tldrawToolId: closestTldrawDefaultColorStyleColor('geo', color),
}))

const DRAW_TOOL: WhiteboardTool = {
  label: __('Draw'),
  shortcut: 'D',
  iconName: 'pen',
  id: 'draw-1',
  tldrawToolId: 'draw',
  secondaryMenu: {
    id: 'color',
    items: createSecondaryMenuFromColors('draw', DRAW_COLORS),
    defaultSelection: DEFAULT_COLORS.draw,
  },
}
const HIGHLIGHT_TOOL: WhiteboardTool = {
  label: __('Highlight'),
  shortcut: 'H',
  iconName: 'highlighter',
  id: 'highlight',
  tldrawToolId: 'highlight',
  secondaryMenu: {
    id: 'color',
    items: createSecondaryMenuFromColors('highlight', HIGHLIGHTER_COLORS),
    defaultSelection: DEFAULT_COLORS.highlighter,
  },
}
const ERASER_TOOL: WhiteboardTool = {
  label: __('Eraser'),
  shortcut: 'E',
  iconName: 'eraser',
  id: 'eraser',
  tldrawToolId: 'padlet-draw-eraser',
}
/**
 * All tools available in the whiteboard
 */
const ALL_TOOLS: WhiteboardTool[] = [
  {
    label: __('Select'),
    shortcut: 'V',
    iconName: 'cursor',
    id: 'select',
    tldrawToolId: 'select',
  },
  {
    label: __('Interact'),
    shortcut: 'I',
    iconName: 'pointer',
    id: 'interact',
    tldrawToolId: 'interact',
  },
  {
    label: __('Draw'),
    iconName: 'draw-nested',
    id: 'draw-nested',
    tldrawToolId: 'draw-nested',
    secondaryMenu: {
      id: 'draw-tool',
      items: [DRAW_TOOL, HIGHLIGHT_TOOL, ERASER_TOOL],
      defaultSelection: DRAW_TOOL.id,
    },
  },
  {
    label: __('Pointer'),
    shortcut: 'P',
    iconName: 'laser_pointer',
    id: 'laser',
    tldrawToolId: 'laser',
  },
  // Include them here to allow selectedTool to be set to them and for toolStates object
  // they are not in toolSections so they won't be displayed in the toolbar
  { ...DRAW_TOOL, shortcut: '' },
  { ...HIGHLIGHT_TOOL, shortcut: '' },
  { ...ERASER_TOOL, shortcut: '' },

  {
    label: 'Geometry',
    iconName: 'geo',
    id: 'geo',
    tldrawToolId: 'geo',
    secondaryMenu: {
      id: 'geo-shape',
      items: [
        {
          label: __('Ellipse'),
          shortcut: 'O',
          iconName: 'geo_ellipse',
          id: 'ellipse',
          tldrawToolId: 'ellipse',
        },
        {
          label: __('Rectangle'),
          shortcut: 'R',
          iconName: 'geo_rectangle',
          id: 'rectangle',
          tldrawToolId: 'rectangle',
        },
        {
          label: __('Cloud'),
          iconName: 'geo_cloud',
          id: 'cloud',
          tldrawToolId: 'cloud',
        },
        {
          label: __('Oval'),
          iconName: 'geo_oval',
          id: 'oval',
          tldrawToolId: 'oval',
        },
        {
          label: __('Arrow'),
          iconName: 'geo_arrow_right',
          id: 'arrow-right',
          tldrawToolId: 'arrow-right',
        },
        {
          label: __('Star'),
          iconName: 'geo_star',
          id: 'star',
          tldrawToolId: 'star',
        },
        {
          label: __('Heart'),
          iconName: 'geo_heart',
          id: 'heart',
          tldrawToolId: 'heart',
        },
      ],
      defaultSelection: 'rectangle',
    },
  },
  {
    label: __('Note'),
    shortcut: 'N',
    iconName: 'postnote',
    id: 'note',
    tldrawToolId: 'note',
    secondaryMenu: {
      id: 'color',
      items: createSecondaryMenuFromColors('note', NOTES_COLORS),
      defaultSelection: DEFAULT_COLORS.note,
    },
  },
  {
    label: __('Text'),
    shortcut: 'T',
    iconName: 'text',
    id: 'text',
    tldrawToolId: 'text',
    secondaryMenu: {
      id: 'color',
      items: createSecondaryMenuFromColors('text', TEXT_COLORS),
      defaultSelection: DEFAULT_COLORS.text,
    },
  },
  {
    label: __('Upload'),
    iconName: 'upload',
    id: 'padlet-upload',
    tldrawToolId: 'padlet-upload',
  },
  {
    label: __('Camera'),
    iconName: 'camera',
    id: 'padlet-camera',
    tldrawToolId: 'padlet-camera',
  },
  {
    label: __('Attachment'),
    iconName: 'attachment',
    id: 'padlet-attachment',
    tldrawToolId: 'padlet-attachment',
  },

  {
    label: __('Undo'),
    shortcut: '',
    iconName: 'undo',
    id: 'undo',
    tldrawToolId: 'undo',
  },
  {
    label: __('Redo'),
    shortcut: '',
    iconName: 'redo',
    id: 'redo',
    tldrawToolId: 'redo',
  },
]

const ALL_TOOLS_AND_SECONDARY_TOOLS = ALL_TOOLS.concat(
  ALL_TOOLS.filter((tool) => tool.secondaryMenu != null).flatMap((tool) => tool.secondaryMenu?.items ?? []),
)

const ALL_TOOL_SECTIONS = [
  ['select', 'laser'],
  ['draw-nested', 'text', 'note', 'geo', 'padlet-upload', 'padlet-attachment'],
  ['undo', 'redo'],
]
function getTldrawToolId(toolId: string): string | undefined {
  return ALL_TOOLS_AND_SECONDARY_TOOLS.find((tool) => tool.id === toolId)?.tldrawToolId
}

function getLocalToolId(tldrawToolId: string): string | undefined {
  return ALL_TOOLS_AND_SECONDARY_TOOLS.find((tool) => tool.tldrawToolId === tldrawToolId)?.id
}

// Actions

enum ActionType {
  Undo = 'Undo',
  Redo = 'Redo',
  ToggleSnapOn = 'ToggleSnapOn',
  ToggleSnapOff = 'ToggleSnapOff',
  SendToFront = 'SendToFront',
  SendToBack = 'SendToBack',
  Link = 'Link',
  Cut = 'Cut',
  Copy = 'Copy',
  Paste = 'Paste',
  Duplicate = 'Duplicate',
  ToggleFreeze = 'ToggleFreeze',
  Delete = 'Delete',
  SelectAll = 'SelectAll',
  GoToPreviousPage = 'GoToPreviousPage',
  GoToNextPage = 'GoToNextPage',
}

const actionShortcuts = [
  {
    actionType: ActionType.Undo,
    kbd: '$mod+z',
  },
  {
    actionType: ActionType.Redo,
    kbd: '$mod+Shift+z',
  },
  {
    actionType: ActionType.SendToFront,
    kbd: ']',
  },
  {
    actionType: ActionType.SendToBack,
    kbd: '[',
  },
  {
    actionType: ActionType.Link,
    kbd: 'Shift+l',
  },
  {
    actionType: ActionType.Cut,
    kbd: '$mod+x',
  },
  {
    actionType: ActionType.Copy,
    kbd: '$mod+c',
  },
  {
    actionType: ActionType.Paste,
    kbd: '$mod+v',
  },
  {
    actionType: ActionType.Duplicate,
    kbd: '$mod+d',
  },
  {
    actionType: ActionType.ToggleFreeze,
    kbd: 'Shift+f',
  },
  {
    actionType: ActionType.Delete,
    kbd: 'Backspace',
  },
  {
    actionType: ActionType.SelectAll,
    kbd: '$mod+a',
  },
]

// Adapted from: https://github.com/tldraw/tldraw/blob/v2.1.4/packages/tldraw/src/lib/ui/context/actions.tsx
// Previously tried to set the output of useActions on window but the behavior was very buggy
const handleAction = (actionType: ActionType, checkCanEdit: (shape: TLShape) => boolean): void => {
  const tldrawEditor = getEditorObject()
  const selectedShapes = tldrawEditor?.getSelectedShapes() ?? []
  // We only allow actions on the least permissive of the selected objects
  const areAllSelectedShapesEditable = selectedShapes.every(checkCanEdit)

  switch (actionType) {
    case ActionType.Undo: {
      tldrawEditor?.undo()
      break
    }
    case ActionType.Redo: {
      tldrawEditor?.redo()
      break
    }
    case ActionType.ToggleSnapOn: {
      tldrawEditor?.user.updateUserPreferences({ isSnapMode: true })
      break
    }
    case ActionType.ToggleSnapOff: {
      tldrawEditor?.user.updateUserPreferences({ isSnapMode: false })
      break
    }
    case ActionType.SendToFront: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      tldrawEditor?.mark('bring to front')
      tldrawEditor?.bringToFront(selectedShapes)
      break
    }
    case ActionType.SendToBack: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      tldrawEditor?.mark('send to back')
      tldrawEditor?.sendToBack(selectedShapes)
      break
    }
    case ActionType.Cut: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      tldrawEditor?.mark('cut')
      window.tldrawMenuClipboardEvents?.cut('unknown')
      break
    }
    case ActionType.Copy: {
      tldrawEditor?.mark('copy')
      window.tldrawMenuClipboardEvents?.copy('unknown')
      break
    }
    case ActionType.Paste: {
      void navigator.clipboard?.read().then((clipboardItems) => {
        void window.tldrawMenuClipboardEvents?.paste(clipboardItems, 'unknown', tldrawEditor?.inputs.currentPagePoint)
      })
      break
    }
    case ActionType.Duplicate: {
      tldrawEditor?.mark('duplicate shapes')
      tldrawEditor?.duplicateShapes(selectedShapes, { x: 20, y: 20 })
      break
    }
    case ActionType.ToggleFreeze: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      tldrawEditor?.mark('toggle freeze')
      tldrawEditor?.toggleLock(selectedShapes)
      break
    }
    case ActionType.Delete: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      tldrawEditor?.mark('delete')
      tldrawEditor?.deleteShapes(selectedShapes)
      break
    }
    case ActionType.SelectAll: {
      tldrawEditor?.mark('select all')
      tldrawEditor?.selectAll()
      break
    }
    case ActionType.GoToPreviousPage: {
      const previousPage = tldrawEditor
        ?.getPages()
        .find((_page, index, pages) => pages[index + 1]?.id === tldrawEditor?.getCurrentPageId())
      if (previousPage == null) return
      tldrawEditor?.setCurrentPage(previousPage.id)
      break
    }
    case ActionType.GoToNextPage: {
      const nextPage = tldrawEditor
        ?.getPages()
        .find((_page, index, pages) => pages[index - 1]?.id === tldrawEditor?.getCurrentPageId())
      if (nextPage == null) return
      tldrawEditor?.setCurrentPage(nextPage.id)
      break
    }
    default:
      break
  }
}

// Frames

const FRAME_ID_QUERY_PARAM = 'frame_id'

interface PageMeta extends JsonObject {
  backgroundType?: BackgroundType
  framePermissionOverride?: FramePermissionOverride
  customBackgroundUrl?: string
}

/**
 * Determine the new index key based on surrounding pages.
 */
const getNewIndexKey = (pageBefore: TLPage | null, pageAfter: TLPage | null): IndexKey => {
  if (pageBefore != null && pageAfter != null) {
    return getIndexBetween(pageBefore.index, pageAfter.index)
  }
  if (pageBefore != null) {
    return getIndexAbove(pageBefore.index)
  }
  if (pageAfter != null) {
    return getIndexBelow(pageAfter.index)
  }

  throw new Error('Unable to determine new index key; both pageBefore and pageAfter are null')
}

/**
 * Calculate a new index key for moving a frame from one position to another.
 *
 * The fromIndex is used to determine whether the generated index key comes before or after
 * the index key that currently occupies the toIndex.
 *
 * Example:
 * - If moving a frame from index 3 to index 2, generate an IndexKey that is between index 1 and 2.
 * - If moving a frame from index 1 to index 2, generate an IndexKey that is between index 2 and 3.
 */
const calculateIndexKeyForMoveFrame = (fromIndex: number, toIndex: number): IndexKey | null => {
  const editor = getEditorObject()
  const pages = editor?.getPages()
  if (pages == null) {
    return null
  }

  const isMovingToLowerIndex = fromIndex > toIndex

  const pageBefore = isMovingToLowerIndex ? pages[toIndex - 1] : pages[toIndex]
  const pageAfter = isMovingToLowerIndex ? pages[toIndex] : pages[toIndex + 1]

  return getNewIndexKey(pageBefore, pageAfter)
}

/**
 * Calculate a new index key for creating a new frame at a specified position (toIndex).
 *
 * Example:
 * - If inserting a new frame at index 2, generate an IndexKey that is between index 1 and 2.
 */
const calculateIndexKeyForNewFrame = (toIndex: number): IndexKey | null => {
  const editor = getEditorObject()
  const pages = editor?.getPages()
  if (pages == null) {
    return null
  }

  const pageBefore = pages[toIndex - 1]
  const pageAfter = pages[toIndex]

  return getNewIndexKey(pageBefore, pageAfter)
}

const moveFrame = (frameId: TLPageId, fromIndex: number, toIndex: number): void => {
  const toIndexKey = calculateIndexKeyForMoveFrame(fromIndex, toIndex)
  if (toIndexKey == null) {
    throw new Error('toIndexKey not found')
  }

  const editor = getEditorObject()
  if (editor == null) return

  editor.mark('moving page')
  editor.updatePage({
    id: frameId,
    index: toIndexKey,
  })
}

function addFrame(destinationIndex: number): void {
  const editor = getEditorObject()
  if (editor == null) return

  const newPageIndexKey = calculateIndexKeyForNewFrame(destinationIndex)
  if (newPageIndexKey == null) {
    return
  }

  const newPageId = PageRecordType.createId()
  editor.createPage({ name: 'Page', index: newPageIndexKey, id: newPageId })
  editor.setCurrentPage(newPageId)
}

function deleteFrame(frameId: TLPageId): void {
  const editor = getEditorObject()
  if (editor == null) return
  const shapeIdsForPage = editor.getPageShapeIds(frameId)

  editor.mark('delete page and shapes')
  // We need to batch because deletePage does not automatically delete shapes
  // so they are orphaned in the store and it causes inconsistent undo behavior
  // (probably due to garbage collection)
  editor.batch(() => {
    editor.deleteShapes([...shapeIdsForPage])
    editor.deletePage(frameId)
  })
}

// Backgrounds

enum BackgroundType {
  // Standard
  Plain = 'Plain',
  DotGrid = 'DotGrid',
  Grid = 'Grid',
  Ruled = 'Ruled',
  Frosted = 'Frosted',

  // Template
  VennDiagram = 'VennDiagram',
  WeekdaysCalendar = 'WeekdaysCalendar',
  WeeklyCalendar = 'WeeklyCalendar',
  MonthlyCalendar = 'MonthlyCalendar',
  Timeline = 'Timeline',
  OneHundredChart = 'OneHundredChart',
  TwoColumns = 'TwoColumns',
  ThreeColumns = 'ThreeColumns',
  FourColumns = 'FourColumns',
  FiveColumns = 'FiveColumns',
  FourCorners = 'FourCorners',
  Blueprint = 'Blueprint',
  Chalkboard = 'Chalkboard',
  Custom = 'Custom',
}

const STANDARD_BACKGROUND_TYPES = [
  BackgroundType.Plain,
  BackgroundType.Ruled,
  BackgroundType.DotGrid,
  BackgroundType.Grid,
  BackgroundType.Frosted,
]
const TEMPLATE_BACKGROUND_TYPES = [
  BackgroundType.VennDiagram,
  BackgroundType.WeekdaysCalendar,
  BackgroundType.WeeklyCalendar,
  BackgroundType.MonthlyCalendar,
  BackgroundType.Timeline,
  BackgroundType.OneHundredChart,
  BackgroundType.TwoColumns,
  BackgroundType.ThreeColumns,
  BackgroundType.FourColumns,
  BackgroundType.FiveColumns,
  BackgroundType.FourCorners,
]

// Background svgs are uploaded to this bucket:
// https://console.cloud.google.com/storage/browser/padlet-static-production/whiteboard/frame-backgrounds?project=logical-handler-867
const BACKGROUND_TYPE_TO_URL_MAPPING: { [key in BackgroundType]: string } = {
  // 128x128

  [BackgroundType.Plain]: 'https://padlet.net/whiteboard/frame-backgrounds/plain_background.svg',
  [BackgroundType.Grid]: 'https://padlet.net/whiteboard/frame-backgrounds/grid_background.svg',
  [BackgroundType.Ruled]: 'https://padlet.net/whiteboard/frame-backgrounds/ruled_background.svg',
  [BackgroundType.Frosted]: 'https://padlet.net/whiteboard/frame-backgrounds/frosted_glass.svg',

  // 1920x1080

  // For DotGrid, we tried to use 128x128 but the browser makes it slightly deformed at the edges when it repeats
  [BackgroundType.DotGrid]: 'https://padlet.net/whiteboard/frame-backgrounds/dot_background.svg',

  [BackgroundType.VennDiagram]: 'https://padlet.net/whiteboard/frame-backgrounds/venn_diagram.svg',
  [BackgroundType.WeekdaysCalendar]: 'https://padlet.net/whiteboard/frame-backgrounds/weekdays_calendar.svg',
  [BackgroundType.WeeklyCalendar]: 'https://padlet.net/whiteboard/frame-backgrounds/weekly_calendar.svg',
  [BackgroundType.MonthlyCalendar]: 'https://padlet.net/whiteboard/frame-backgrounds/monthly_calendar.svg',
  [BackgroundType.Timeline]: 'https://padlet.net/whiteboard/frame-backgrounds/timeline.svg',
  [BackgroundType.OneHundredChart]: 'https://padlet.net/whiteboard/frame-backgrounds/one_hundred_chart.svg',
  [BackgroundType.TwoColumns]: 'https://padlet.net/whiteboard/frame-backgrounds/two_columns.svg',
  [BackgroundType.ThreeColumns]: 'https://padlet.net/whiteboard/frame-backgrounds/three_columns.svg',
  [BackgroundType.FourColumns]: 'https://padlet.net/whiteboard/frame-backgrounds/four_columns.svg',
  [BackgroundType.FiveColumns]: 'https://padlet.net/whiteboard/frame-backgrounds/five_columns.svg',
  [BackgroundType.FourCorners]: 'https://padlet.net/whiteboard/frame-backgrounds/four_corners.svg',
  [BackgroundType.Custom]: '',

  // From Jamboard
  [BackgroundType.Blueprint]: 'https://padlet.net/whiteboard/frame-backgrounds/blueprint.svg',
  [BackgroundType.Chalkboard]: 'https://padlet.net/whiteboard/frame-backgrounds/chalkboard.svg',
}

const BACKGROUND_TYPE_TO_PREVIEW_URL_MAPPING: { [key in BackgroundType]: string } = {
  ...BACKGROUND_TYPE_TO_URL_MAPPING,
  // We use a separate (smaller) image for the dot grid preview otherwise the dots will not be visible
  [BackgroundType.DotGrid]: 'https://padlet.net/whiteboard/frame-backgrounds/dot_background_preview.svg',
}

// Permissions

enum FramePermissionOverride {
  None = 'None',
  Reader = 'Reader',
  Writer = 'Writer',
  Editor = 'Editor',
}

// We don't need a No access permission level because the visitor gets blocked at the route level instead
// I.e. they do not even load surface
enum PermissionLevel {
  Reader = 'Reader',
  Writer = 'Writer',
  Editor = 'Editor',
  Manager = 'Manager',
  Administrator = 'Administrator',
  Owner = 'Owner',
}

// Refer to table for breakdown of what each permission can do:
// https://whimsical.com/permissions-technical-representation-72rGSxT28JDQhMMGGEtEo6
interface Permissions {
  canRead: boolean
  canWrite: boolean
  canEdit: boolean
  canManage: boolean
  canAdminister: boolean
  canTrash: boolean
}

const FRAME_PERMISSION_OVERRIDE_MAPPING: { [key in FramePermissionOverride]: PermissionLevel | null } = {
  [FramePermissionOverride.None]: null,
  [FramePermissionOverride.Reader]: PermissionLevel.Reader,
  [FramePermissionOverride.Writer]: PermissionLevel.Writer,
  [FramePermissionOverride.Editor]: PermissionLevel.Editor,
}

const PERMISSIONS_MAPPING: { [key in PermissionLevel]: Permissions } = {
  [PermissionLevel.Reader]: {
    canRead: true,
    canWrite: false,
    canEdit: false,
    canManage: false,
    canAdminister: false,
    canTrash: false,
  },
  [PermissionLevel.Writer]: {
    canRead: true,
    canWrite: true,
    canEdit: false,
    canManage: false,
    canAdminister: false,
    canTrash: false,
  },
  [PermissionLevel.Editor]: {
    canRead: true,
    canWrite: true,
    canEdit: true,
    canManage: false,
    canAdminister: false,
    canTrash: false,
  },
  [PermissionLevel.Manager]: {
    canRead: true,
    canWrite: true,
    canEdit: true,
    canManage: true,
    canAdminister: false,
    canTrash: false,
  },
  [PermissionLevel.Administrator]: {
    canRead: true,
    canWrite: true,
    canEdit: true,
    canManage: true,
    canAdminister: true,
    canTrash: false,
  },
  [PermissionLevel.Owner]: {
    canRead: true,
    canWrite: true,
    canEdit: true,
    canManage: true,
    canAdminister: true,
    canTrash: true,
  },
}

const getFramePermissionOverride = (frameId: TLPageId): FramePermissionOverride => {
  const editor = getEditorObject()
  const frame = editor?.getPage(frameId)
  const frameMeta: PageMeta | undefined = frame?.meta
  return frameMeta?.framePermissionOverride ?? FramePermissionOverride.None
}

const getFramePermissions = (frameId: TLPageId): Permissions | null => {
  const framePermissionOverride = getFramePermissionOverride(frameId)
  const permissionLevel = FRAME_PERMISSION_OVERRIDE_MAPPING[framePermissionOverride]
  return permissionLevel != null ? PERMISSIONS_MAPPING[permissionLevel] : null
}

const isShapeApproved = (record: TLRecord | null | undefined): boolean => {
  if (record == null) {
    return true
  }
  // We don't moderate non-shape records
  if (record.typeName !== 'shape') {
    return true
  }

  return typeof record?.meta.approved === 'boolean' ? record.meta.approved : true
}

const approveShape = (shape: TLShape): void => {
  const editor = getEditorObject()
  editor?.updateShape({
    ...shape,
    meta: {
      ...shape.meta,
      approved: true,
    },
  })
  editor?.selectNone()
}

export {
  actionShortcuts,
  ActionType,
  addFrame,
  ALL_TOOL_SECTIONS,
  ALL_TOOLS,
  approveShape,
  BACKGROUND_TYPE_TO_PREVIEW_URL_MAPPING,
  BACKGROUND_TYPE_TO_URL_MAPPING,
  BackgroundType,
  calculateIndexKeyForMoveFrame,
  calculateIndexKeyForNewFrame,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  changeAllTldrawDefaultColorStyleColor,
  DEFAULT_COLORS,
  deleteFrame,
  FRAME_ID_QUERY_PARAM,
  FRAME_PERMISSION_OVERRIDE_MAPPING,
  FramePermissionOverride,
  geoToolColors,
  getFramePermissionOverride,
  getFramePermissions,
  getLocalToolId,
  getTailwindBgColorClass,
  getTailwindColorClass,
  getTailwindRingColorClass,
  getTldrawToolId,
  handleAction,
  HIGHLIGHTER_COLORS,
  isShapeApproved,
  MAX_FRAME_LIMIT,
  moveFrame,
  NOTES_COLORS,
  PermissionLevel,
  PERMISSIONS_MAPPING,
  SHAPE_BORDER_COLORS,
  SHAPE_BORDER_TO_FAKE_BORDER_COLOR_MAP,
  SHAPE_BORDER_TO_FAKE_FILL_COLOR_MAP,
  STANDARD_BACKGROUND_TYPES,
  TEMPLATE_BACKGROUND_TYPES,
}
export type { PageMeta, Permissions, ToolTailwindColorType }
